// Shared API utility using axios for all frontend API calls
import axios from 'axios';
import { readAuthToken } from './authStorage';
import { guestBranchId } from './branchPreference';
import { getCookieConsent } from './cookieConsent';
import { ensureVisitorId } from './visitor';
const API_BASE_URL = process.env.REACT_APP_API_URL;
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // include cookies for auth if needed
});
const customerAuthPaths = new Set(['/auth/register', '/auth/login', '/auth/mobile/confirm', '/auth/google', '/auth/google/phone-confirm', '/auth/facebook']);

// Add a request interceptor to include the JWT token
api.interceptors.request.use(
  (config) => {
    if (process.env.REACT_APP_PORTAL_ROLE === 'CUSTOMER' && config.method === 'post' && customerAuthPaths.has(config.url)) {
      const branchId = guestBranchId();
      if (branchId && config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) config.data = { ...config.data, branchId };
    }
    if (config.url === '/analytics/visit' && !getCookieConsent().analytics) return Promise.reject(new axios.CanceledError('Analytics permission required.'));
    config.headers = config.headers || {};
    config.headers['X-BigCircle-Portal'] = process.env.REACT_APP_PORTAL_ROLE || 'CUSTOMER';
    try {
      const areaId = process.env.REACT_APP_PORTAL_ROLE === 'ADMIN' ? sessionStorage.getItem('bigcircle:admin-area') : guestBranchId();
      if (areaId) config.headers['X-BigCircle-Area-ID'] = areaId;
    } catch { /* Continue with the server's default area. */ }
    config.headers['X-BigCircle-Visitor-ID'] = ensureVisitorId();
    const token = readAuthToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Generic GET request
export const get = (url, config = {}) => api.get(url, config).then(res => res.data);

// Generic POST request
export const post = (url, data, config = {}) => api.post(url, data, config).then(res => res.data);

// Generic PUT request
export const put = (url, data, config = {}) => api.put(url, data, config).then(res => res.data);

// Generic PATCH request
export const patch = (url, data, config = {}) => api.patch(url, data, config).then(res => res.data);

// Generic DELETE request
export const del = (url, config = {}) => api.delete(url, config).then(res => res.data);

export default api;

api.interceptors.response.use(response => {
  if ((response.config.headers?.Authorization || '') === (readAuthToken() ? 'Bearer ' + readAuthToken() : '') && response.config.url?.startsWith('/cart') && Array.isArray(response.data?.cart)) {
    window.dispatchEvent(new CustomEvent('cart-updated', { detail: response.data.cart }));
  }
  if (response.data?.cartAdjustments?.length) {
    window.dispatchEvent(new CustomEvent('cart-adjusted', { detail: response.data.cartAdjustments }));
  }
  return response;
});
